/* StreakBadge — top-bar pill показващ "🔥 N дни подред записвате храна".
 *
 * Цели:
 *   • Усилва ежедневния habit-loop на пациента (gamification без натрапчивост)
 *   • Веднага видимо в desktop AppShell header slot
 *   • Auto-refresh при `meal-log-changed` / `meal-added` browser event
 *     (същите events, които използва DigitalTwinPanel)
 *
 * UX правила:
 *   • Показваме само ако streak ≥ 2 (1 ден не е "streak" — само поощряваме)
 *   • Ако streak ≥ 2 но не е логнато днес → indicator "в опасност" (amber/опасност)
 *   • При hover/tap → tooltip с обяснение
 *
 * Backend: GET /api/rewards/meal-streak → { streak, logged_today }
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Flame } from "lucide-react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

export default function StreakBadge({ compact = false }) {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [streak, setStreak] = useState(0);
  const [loggedToday, setLoggedToday] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const fetchStreak = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/rewards/meal-streak`);
      setStreak(data?.streak ?? 0);
      setLoggedToday(!!data?.logged_today);
    } catch {
      // Silent — badge просто не се показва при грешка
    }
  }, [API, user]);

  useEffect(() => {
    fetchStreak();
    const refresh = () => fetchStreak();
    window.addEventListener("meal-log-changed", refresh);
    window.addEventListener("meal-added", refresh);
    return () => {
      window.removeEventListener("meal-log-changed", refresh);
      window.removeEventListener("meal-added", refresh);
    };
  }, [fetchStreak]);

  // Показваме само ако има реален streak (≥ 2 дни)
  if (streak < 2) return null;

  const atRisk = !loggedToday;

  // Цветова палитра според състояние
  const colorCls = atRisk
    ? (isDark
        ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
        : "bg-amber-100 text-amber-700 border-amber-300")
    : (isDark
        ? "bg-orange-500/20 text-orange-300 border-orange-500/40"
        : "bg-orange-100 text-orange-700 border-orange-300");

  const label = bg
    ? `${streak} ${streak === 1 ? "ден" : "дни"} подред`
    : language === "es"
    ? `${streak} ${streak === 1 ? "día" : "días"} seguidos`
    : `${streak} day${streak === 1 ? "" : "s"} streak`;

  const tooltipText = atRisk
    ? (bg
        ? "Логни поне една храна днес, за да запазиш серията!"
        : language === "es"
        ? "¡Registra al menos una comida hoy para mantener la racha!"
        : "Log at least one meal today to keep your streak alive!")
    : (bg
        ? `Записвате храна ${streak} дни подред. Продължавайте така!`
        : language === "es"
        ? `Has registrado comidas ${streak} días seguidos. ¡Sigue así!`
        : `You've logged meals ${streak} days in a row. Keep it up!`);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setShowTooltip((v) => !v)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border backdrop-blur-sm transition-all hover:scale-105 cursor-pointer ${colorCls}`}
        data-testid="streak-badge"
        aria-label={tooltipText}
      >
        <Flame
          className={`w-3 h-3 ${atRisk ? "" : "animate-pulse"}`}
          aria-hidden="true"
        />
        {compact ? streak : label}
      </button>

      {showTooltip && (
        <div
          role="tooltip"
          className={`absolute top-full right-0 mt-1.5 w-56 z-50 px-3 py-2 rounded-lg text-[11px] font-medium shadow-lg pointer-events-none
            ${isDark ? "bg-gray-900 text-gray-100 border border-white/10"
                    : "bg-white text-gray-700 border border-gray-200"}`}
          data-testid="streak-badge-tooltip"
        >
          {tooltipText}
        </div>
      )}
    </div>
  );
}
